/**
 * Honest review-request templates and wording linter (Spec §9.2).
 * Blocks incentive, 5-star, and conditional / gating language.
 */

export const REVIEW_PLATFORMS = [
  'google',
  'facebook',
  'yelp',
  'angi',
  'nextdoor',
  'bbb',
  'other',
  'testimonial_page',
] as const;
export type ReviewPlatform = (typeof REVIEW_PLATFORMS)[number];

export const REVIEW_CHANNELS = ['text', 'email', 'in_person'] as const;
export type ReviewChannel = (typeof REVIEW_CHANNELS)[number];

export const REVIEW_TONES = ['friendly', 'professional', 'short'] as const;
export type ReviewTone = (typeof REVIEW_TONES)[number];

/** Phrases that must never appear in review-request copy. */
const FORBIDDEN_PATTERNS: Array<{ re: RegExp; reason: string }> = [
  { re: /\b5[\s-]?star\b/i, reason: 'Do not ask for a 5-star review' },
  { re: /\bfive[\s-]?star\b/i, reason: 'Do not ask for a five-star review' },
  { re: /\bonly\s+if\s+(you('re| are)\s+)?(happy|satisfied|pleased)\b/i, reason: 'Do not gate reviews on satisfaction' },
  { re: /\bif\s+you('re| are)\s+(happy|satisfied|pleased).{0,40}\b(leave|post|write|share)\b/i, reason: 'Do not gate reviews on satisfaction' },
  { re: /\b(discount|gift|coupon|rebate|free\s+service|incentive|reward|credit)\b/i, reason: 'Do not offer incentives for reviews' },
  { re: /\bin\s+exchange\s+for\b/i, reason: 'Do not offer something in exchange for a review' },
  { re: /\bonly\s+(post|leave|write)\b.{0,40}\bif\b/i, reason: 'Do not filter who may leave a review' },
  { re: /\bpositive\s+(review|feedback)\s+only\b/i, reason: 'Do not request only positive feedback' },
];

export type LintResult = { ok: true } | { ok: false; violations: string[] };

export function lintReviewRequestCopy(text: string): LintResult {
  const violations: string[] = [];
  for (const p of FORBIDDEN_PATTERNS) {
    if (p.re.test(text)) violations.push(p.reason);
  }
  return violations.length ? { ok: false, violations: [...new Set(violations)] } : { ok: true };
}

export type MessageGenerateInput = {
  channel: ReviewChannel;
  tone: ReviewTone;
  businessName: string;
  reviewUrl: string;
  customerFirstName?: string;
  jobType?: string;
  personalLine?: string;
};

export type MessageVariant = { id: string; label: string; body: string };

function greeting(name?: string): string {
  const n = name?.trim();
  return n ? `Hi ${n}` : 'Hi';
}

function jobBit(jobType?: string): string {
  const j = jobType?.trim();
  return j ? ` for your recent ${j}` : '';
}

/** Two neutral variants per channel × tone. Never invents incentives or star asks. */
export function generateReviewMessages(input: MessageGenerateInput): MessageVariant[] {
  const g = greeting(input.customerFirstName);
  const job = jobBit(input.jobType);
  const personal = input.personalLine?.trim();
  const personalBit = personal ? ` ${personal}` : '';
  const biz = input.businessName.trim() || 'our team';
  const url = input.reviewUrl.trim();

  const templates: Record<ReviewChannel, Record<ReviewTone, [string, string]>> = {
    text: {
      friendly: [
        `${g} — thanks again${job}.${personalBit} If you have a moment, we'd value your honest feedback: ${url}`,
        `${g}, this is ${biz}.${personalBit} When you have a minute, we'd appreciate your honest review: ${url}`,
      ],
      professional: [
        `${g}, thank you${job}. We'd value your honest feedback when convenient: ${url}`,
        `${g} — ${biz} appreciates your business${job}. Your honest review helps other homeowners: ${url}`,
      ],
      short: [
        `${g} — thanks${job}. Honest feedback welcome: ${url}`,
        `Thanks${job}. Leave honest feedback anytime: ${url}`,
      ],
    },
    email: {
      friendly: [
        `${g},\n\nThanks again${job}.${personalBit}\n\nWe'd value your honest feedback — positive or constructive. Here's the link:\n${url}\n\nThank you,\n${biz}`,
        `${g},\n\nIt was a pleasure working with you${job}.${personalBit}\n\nIf you're willing, please share your honest experience:\n${url}\n\n— ${biz}`,
      ],
      professional: [
        `${g},\n\nThank you for choosing ${biz}${job}. We welcome your honest feedback:\n${url}\n\nSincerely,\n${biz}`,
        `${g},\n\nWe appreciate your business${job}. Your candid review helps other customers make informed decisions:\n${url}\n\nRegards,\n${biz}`,
      ],
      short: [
        `${g},\n\nThanks${job}. Honest feedback link:\n${url}\n\n— ${biz}`,
        `${g},\n\nWe'd value your honest review:\n${url}\n\n${biz}`,
      ],
    },
    in_person: {
      friendly: [
        `${g}, thanks so much${job}.${personalBit} If you're open to it, we'd value your honest feedback — you can scan this QR or use this link later: ${url}`,
        `Hey — really appreciate you${job}.${personalBit} No pressure, but if you ever want to leave an honest review, here's how: ${url}`,
      ],
      professional: [
        `Thank you for your business${job}. If you have a moment later, we'd value your honest feedback at ${url}.`,
        `We appreciate the opportunity to work with you${job}. You're welcome to share an honest review anytime: ${url}.`,
      ],
      short: [
        `Thanks${job}. Honest feedback anytime: ${url}`,
        `Appreciate you. Review link when convenient: ${url}`,
      ],
    },
  };

  const pair = templates[input.channel][input.tone];
  const variants: MessageVariant[] = [
    { id: 'a', label: 'Variant A', body: pair[0].replace(/\n{3,}/g, '\n\n').trim() },
    { id: 'b', label: 'Variant B', body: pair[1].replace(/\n{3,}/g, '\n\n').trim() },
  ];

  for (const v of variants) {
    const lint = lintReviewRequestCopy(v.body);
    if (!lint.ok) {
      throw new Error(`Template failed honesty lint: ${lint.violations.join('; ')}`);
    }
  }
  return variants;
}

export function smsHref(body: string): string {
  return `sms:?&body=${encodeURIComponent(body)}`;
}

export function mailtoHref(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** Basic URL shape check for review destination links. */
export function isValidReviewDestinationUrl(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
    if (!u.hostname.includes('.')) return false;
    return true;
  } catch {
    return false;
  }
}
